import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useAuth } from '../../context/authContext';
import { disconnectGmail, getGmailConnectUrl, getGmailStatus } from '../../api/googleIntegration';

/** System Email (Gmail) settings (Phase 81) — connects exactly ONE
 * system-level Google account (info@imcedu.com by default) used to send
 * IMS-originated email via the Gmail API, completely separate from the
 * per-counselor "My Account" Google Calendar connection. Admin-only
 * (master_data:update to connect/disconnect, :view to see status) —
 * mirrors MyAccountPage.tsx's connect/disconnect card shape, since that's
 * this app's existing pattern for a Google-connect UI. */
export const SystemEmailSettingsPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('master_data', 'update');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectError, setConnectError] = useState<string | null>(null);
  const [redirectResult] = useState(() => searchParams.get('connected'));
  const [redirectReason] = useState(() => searchParams.get('reason'));

  const statusQuery = useQuery({ queryKey: ['gmail-status'], queryFn: getGmailStatus });

  useEffect(() => {
    if (searchParams.has('connected')) {
      queryClient.invalidateQueries({ queryKey: ['gmail-status'] });
      setSearchParams((params) => {
        params.delete('connected');
        params.delete('reason');
        return params;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectMutation = useMutation({
    mutationFn: getGmailConnectUrl,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => {
      setConnectError(
        isAxiosError(err) && err.response?.status === 400
          ? 'System Gmail integration is not configured yet. Ask your administrator to set it up.'
          : 'Something went wrong. Please try again.',
      );
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectGmail,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gmail-status'] }),
  });

  const justConnected = redirectResult === '1';
  const connectFailed = redirectResult === '0';
  const wrongAccount = connectFailed && redirectReason === 'wrong_account';

  return (
    <Box sx={{ p: 3, maxWidth: 560 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        System Email
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <MailOutlineIcon color="action" />
            <Typography variant="h6">IMC Email: {statusQuery.data?.account ?? '—'}</Typography>
          </Stack>

          {justConnected && (
            <Alert severity="success" sx={{ mb: 2 }}>
              System Gmail connected.
            </Alert>
          )}
          {wrongAccount && (
            <Alert severity="error" sx={{ mb: 2 }}>
              That Google account doesn&apos;t match {statusQuery.data?.account ?? 'the configured system account'}.
              Please sign in to the correct Google Workspace account and try again.
            </Alert>
          )}
          {connectFailed && !wrongAccount && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Couldn&apos;t connect System Email. Please try again.
            </Alert>
          )}
          {connectError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {connectError}
            </Alert>
          )}

          {statusQuery.isLoading ? null : statusQuery.data?.connected ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color="success" label="Connected" />
                <Typography variant="body2" color="text.secondary">
                  {statusQuery.data.email}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                IMS-originated emails (password resets, reminders, document sends, campaigns) are sent from this
                account when connected, falling back to SMTP otherwise.
              </Typography>
              {canManage && (
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    disabled={connectMutation.isPending}
                    onClick={() => connectMutation.mutate()}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Reconnect
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={disconnectMutation.isPending}
                    onClick={() => disconnectMutation.mutate()}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Disconnect
                  </Button>
                </Stack>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label="Not Connected" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Connect the {statusQuery.data?.account ?? 'system'} Google account so IMS can send email via Gmail
                instead of (or in addition to) SMTP. This is a one-time, admin-only connection — individual
                counselors never need to authorize this.
              </Typography>
              {canManage ? (
                <Button
                  variant="contained"
                  disabled={connectMutation.isPending || statusQuery.isLoading}
                  onClick={() => connectMutation.mutate()}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Connect Google Gmail
                </Button>
              ) : (
                <Alert severity="info">Only an Admin/Super Admin can connect System Email.</Alert>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
